import type React from 'react';
import { cn } from '@/lib/utils';
import type { DebtPaymentStatus, MovementType } from '@/types/manager.model';

type ChipStyle = { label: string; className: string };

const TYPE_PILL_BASE =
  'inline-flex items-center rounded-md border px-2 py-0.5 text-[11px] font-semibold uppercase leading-tight tracking-[0.1px]';
const STATUS_PILL_BASE =
  'inline-flex items-center rounded-full border px-2.5 py-0.5 text-[11px] font-semibold';

const StyledPill: React.FC<{
  baseClassName: string;
  style: ChipStyle;
  className?: string;
}> = ({ baseClassName, style, className }) => (
  <span className={cn(baseClassName, style.className, className)}>
    {style.label}
  </span>
);

const TYPE_CHIP_STYLES: Record<MovementType, ChipStyle> = {
  torneo: {
    label: 'Torneo',
    className: 'bg-[#EEF2FF] text-[#3B49B5] border-[#D6DDFB]',
  },
  clase: {
    label: 'Clase',
    className: 'bg-[#FFF1E6] text-[#A8550C] border-[#FCD9B4]',
  },
  partido: {
    label: 'Partido',
    className: 'bg-[#F3EAFB] text-[#6B2FA3] border-[#DDC6F2]',
  },
  transferencia: {
    label: 'Transferencia',
    className: 'bg-[#E8F6F0] text-[#0F7A35] border-[#BFE6CF]',
  },
};

export const TypeChip: React.FC<{ tipo: MovementType; className?: string }> = ({
  tipo,
  className,
}) => (
  <StyledPill
    baseClassName={TYPE_PILL_BASE}
    style={TYPE_CHIP_STYLES[tipo]}
    className={className}
  />
);

const STATUS_BADGE_STYLES: Record<DebtPaymentStatus, ChipStyle> = {
  PENDING_REVIEW: {
    label: 'Pendiente',
    className: 'bg-[#E5EEFF] text-[#1E40AF] border-[#B8CDFB]',
  },
  APPROVED: {
    label: 'Aprobada',
    className: 'bg-[#E6F6EC] text-[#0F7A35] border-[#A5D8B6]',
  },
  REJECTED: {
    label: 'Rechazada',
    className: 'bg-[#FCEAEA] text-[#9F1B1B] border-[#F4B3B3]',
  },
  CANCELLED: {
    label: 'Cancelada',
    className: 'bg-[#EEF0F4] text-[#4B5563] border-[#D5D9E0]',
  },
};

export const TransferStatusBadge: React.FC<{
  status: DebtPaymentStatus;
  className?: string;
}> = ({ status, className }) => (
  <StyledPill
    baseClassName={STATUS_PILL_BASE}
    style={STATUS_BADGE_STYLES[status]}
    className={className}
  />
);

const SETTLEMENT_STYLES: Record<'ACEPTADO' | 'REEMBOLSADO', ChipStyle> = {
  ACEPTADO: {
    label: 'Aceptado',
    className: 'bg-[#E6F6EC] text-[#0F7A35] border-[#A5D8B6]',
  },
  REEMBOLSADO: {
    label: 'Reembolsado',
    className: 'bg-[#EEF0F4] text-[#4B5563] border-[#D5D9E0]',
  },
};

export const SettlementBadge: React.FC<{
  settlement: 'ACEPTADO' | 'REEMBOLSADO';
  className?: string;
}> = ({ settlement, className }) => (
  <StyledPill
    baseClassName={STATUS_PILL_BASE}
    style={SETTLEMENT_STYLES[settlement]}
    className={className}
  />
);
